import { captureException } from '@sentry/react'
import { useEffect, useMemo, useReducer, useState } from 'react'
import { MdInfoOutline } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { IASOAdvanced } from '~/types/aso'

import Badge from '~/components/Badge'
import CollapseButton from '~/components/Buttons/CollapseButton'
import Icon from '~/components/Icon'
import CheckboxInput from '~/components/Inputs/Basic/CheckboxInput'
import RangeInput from '~/components/Inputs/Basic/RangeInput'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import Score from '~/components/Score'
import CustomFlagOption, {
  formatOptionLabel as formatCountryOptionLabel,
} from '~/components/SelectOptions/CustomFlagOption'
import CustomRatingOption, {
  formatOptionLabel as formatRatingOptionLabel,
} from '~/components/SelectOptions/CustomRatingOption'
import Tooltip from '~/components/Tooltip'
import asoCountries from '~/const/aso-countries'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { getCountries } from '~/utils/countries'
import { startsWithFilter } from '~/utils/options'

import './styles.scss'

type Filter = 'competitiveness' | 'country' | 'displayASAColumn' | 'onlyASAKeywords' | 'popularity' | 'rating'

type Actions = {
  type: Filter
  value?: unknown
}

export interface ASOFilterState {
  competitiveness: {
    max: number
    min: number
  }
  country: string
  displayASAColumn: boolean
  onlyASAKeywords: boolean
  popularity: {
    max: number
    min: number
  }
  rating: number
}

type ReducerState = (state: ASOFilterState, action: Actions) => ASOFilterState

const reducer = (state: ASOFilterState, action: Actions) => {
  switch (action.type) {
    case 'competitiveness':
      return {
        ...state,
        competitiveness: action.value as {
          max: number
          min: number
        },
      }
    case 'country':
      return { ...state, country: action.value as string }
    case 'displayASAColumn':
      return { ...state, displayASAColumn: !state.displayASAColumn }
    case 'onlyASAKeywords':
      return { ...state, onlyASAKeywords: !state.onlyASAKeywords }
    case 'popularity':
      return {
        ...state,
        popularity: action.value as {
          max: number
          min: number
        },
      }
    case 'rating':
      return { ...state, rating: Number(action.value) }
    default:
      return state
  }
}

export const initialState = (country?: string | null): ASOFilterState => ({
  competitiveness: {
    max: 5,
    min: 0,
  },
  country: country || 'US',
  displayASAColumn: false,
  onlyASAKeywords: false,
  popularity: {
    max: 100,
    min: 0,
  },
  rating: 5,
})

interface Props {
  clusterRankingViewUrl: string
  country?: string | null
  metadata?: IASOAdvanced['metadata']
  onFilterChange: (state: ASOFilterState) => void
}

const ASOFilters = ({ country, metadata, onFilterChange }: Props): ReactElement => {
  /** Local state */

  const { dispatchToSearchParams } = useSearchParamsContext()

  const [state, dispatch] = useReducer<ReducerState>(reducer, initialState(country))

  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false)

  /** Handlers */

  const toggleCollapseFilters = () => {
    setIsFiltersCollapsed(!isFiltersCollapsed)
  }

  const handleFilterChanged = (filter: Filter) => {
    return (filterValue: unknown) => {
      switch (filter) {
        case 'country':
          dispatch({ type: filter, value: (filterValue as IOption).value })
          dispatchToSearchParams({ type: 'update', value: { country: (filterValue as IOption).value } })
          break
        case 'rating':
          dispatch({ type: filter, value: (filterValue as IOption).value })
          break
        case 'displayASAColumn':
          dispatch({ type: filter })
          break
        case 'onlyASAKeywords':
          dispatch({ type: filter })
          break
        case 'popularity':
          dispatch({ type: filter, value: filterValue as { max: number; min: number } })
          break
        case 'competitiveness':
          dispatch({ type: filter, value: filterValue as { max: number; min: number } })
          break
        default:
          captureException(new Error(`Unknown filter type: ${filter}`))
      }
    }
  }

  const countryOptions = useMemo(
    () => [...getCountries().filter(({ value }) => asoCountries.includes(value)), { label: 'Worldwide', value: 'WW' }],
    []
  )

  const ratingOptions = useMemo(() => {
    const tmp = [...Array(6)].map((__, index) => ({ label: `${index}`, value: index })).reverse()
    tmp.unshift({ label: 'All', value: -1 })
    return tmp
  }, [])

  useEffect(() => {
    onFilterChange(state)
  }, [state, onFilterChange])

  return (
    <div className="aso-header">
      <div className="aso-header__before-collapse">
        <div className="country-filter-container">
          <SelectInput
            className="country-filter"
            components={{ Option: CustomFlagOption }}
            filterOption={startsWithFilter}
            // @ts-expect-error Type 'ReactElement<any, string | JSXElementConstructor<any>>' is not assignable to type 'string'
            getOptionLabel={(option: unknown) => formatCountryOptionLabel(option as IOption)}
            isClearable={false}
            menuClassName="aso-advanced-country-filter__dropdown"
            onChange={handleFilterChanged('country')}
            options={countryOptions}
            value={countryOptions.find(({ value }) => state.country === value)}
          />
        </div>
        {!isFiltersCollapsed && (
          <div className="metadata-section">
            <div className="metadata-section__title">
              CURRENT METADATA
              <Tooltip
                align={{ points: ['bl', 'tl'] }}
                mouseEnterDelay={0.2}
                overlay={
                  <div className="metadata-section__title-tooltip">
                    <p>
                      This is the default app metadata displayed on App Store in the selected country (if the primary
                      language was not defined, it will display metadata of the secondary one).
                    </p>
                    <p>
                      FYI, each country has a default language/locale used to display metadata and one or more secondary
                      languages (sometimes none). If we add more than one language/locale for our app&lsquo;s metadata
                      in App Store Connect, keep in mind that the language in which the metadata is shown to each user
                      is impacted by:
                    </p>
                    <ul>
                      <li>the language of the App Store for their location</li>
                      <li>the language settings on their device</li>
                      <li>the languages we&lsquo;ve added</li>
                      <li>and our primary language selection in App Store Connect.</li>
                    </ul>
                    <p>
                      This means that our app can be ranked on keywords of several languages in one country. More info
                      in the Apple documentation:{' '}
                      <a
                        href="https://help.apple.com/app-store-connect/#/dev997f9cf7c"
                        rel="noreferrer"
                        target="_blank"
                      >
                        https://help.apple.com/app-store-connect/#/dev997f9cf7c
                      </a>
                      .
                    </p>
                  </div>
                }
                placement="topRight"
                trigger={['hover']}
              >
                <div>
                  <MdInfoOutline className="metadata-section__title__tooltip-icon" size={15} />
                </div>
              </Tooltip>
            </div>
            <div className="metadata-section__container">
              <div className="score-section">
                <div className="score-section__header">
                  <Icon className="score-section__header__icon" color="#aeaeae" name="title" size={21} />
                  <span className="score-section__header__title">Title</span>
                  <Score
                    className="score-section__header__score"
                    isMetadata
                    isPercentage={false}
                    total={30}
                    type="title"
                    value={!metadata?.title ? 0 : metadata.title.length > 30 ? 30 : metadata.title.length}
                  />
                </div>
                <div className="score-section__body__app-title">{metadata?.title}</div>
              </div>
              <div className="score-section">
                <div className="score-section__header">
                  <Icon className="score-section__header__icon" color="#aeaeae" name="subtitle" size={21} />
                  <span className="score-section__header__title">Subtitle</span>
                  <Score
                    className="score-section__header__score"
                    isMetadata
                    isPercentage={false}
                    total={30}
                    type="subtitle"
                    value={!metadata?.subtitle ? 0 : metadata.subtitle.length > 30 ? 30 : metadata.subtitle.length}
                  />
                </div>
                <div className="score-section__body__app-subtitle">{metadata?.subtitle}</div>
              </div>
              <div className="score-section">
                <div className="score-section__header">
                  <Icon className="score-section__header__icon" color="#aeaeae" name="keyword" size={21} />
                  <span className="score-section__header__title">Keywords</span>
                  <Score
                    className="score-section__header__score"
                    isMetadata
                    isPercentage={false}
                    total={100}
                    type="keywords"
                    value={
                      !metadata?.keywords
                        ? 0
                        : metadata.keywords.join(',').length > 100
                          ? 100
                          : metadata.keywords.join(',').length
                    }
                  />
                </div>
                <div className="score-section__body__app-keywords">
                  {metadata?.keywords?.map((keyword, idx) => (
                    <Badge className="badge keyword-badge" key={`${keyword}-${idx}`} value={keyword} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        <CollapseButton isCollapsed={isFiltersCollapsed} onClick={toggleCollapseFilters} />
      </div>
      <div className="aso-header__after-collapse">
        <div className="aso-header__after-collapse__filter-quality">
          <SelectInput
            className="filter-rating"
            components={{ Option: CustomRatingOption }}
            // @ts-expect-error Type 'ReactElement<any, string | JSXElementConstructor<any>>' is not assignable to type 'string'
            getOptionLabel={(option: unknown) => formatRatingOptionLabel(option as IOption)}
            isClearable={false}
            label="Keyword quality"
            onChange={handleFilterChanged('rating')}
            options={ratingOptions}
            placeholder="Choose a rating value"
            size="small"
            tooltip={
              <div className="tooltip-content large input-tooltip-container keyword-quality">
                The keyword quality score (from 0 to 5) is a metric computed for each keyword and based on the number of
                competitors that are ranked in top positions (top 10 - top 5). This metric indicates how suitable a
                keyword is to an app (higher score indicates higher relevancy).
              </div>
            }
            value={ratingOptions.find(({ value }) => state.rating === value)}
          />
        </div>
        <div className="aso-header__after-collapse__filter-popularity">
          <RangeInput
            className="filter-range"
            label="Keywords popularity"
            max={100}
            min={0}
            onChange={handleFilterChanged('popularity')}
            tooltip={
              <div className="tooltip-content large input-tooltip-container keyword-popularity">
                The popularity score has a range from 5 to 100. A score of 100 means that the keyword is highly
                searched.
              </div>
            }
          />
        </div>
        <div className="aso-header__after-collapse__filter-competitiveness">
          <RangeInput
            className="filter-range"
            label="Competitiveness"
            max={5}
            min={0}
            onChange={handleFilterChanged('competitiveness')}
            tooltip={
              <div className="tooltip-content large input-tooltip-container keyword-competitiveness">
                The competitiveness is a score for each keyword, between 0 and 5, which indicates our chances of being
                well ranked on the keyword.
              </div>
            }
          />
        </div>
        <div className="aso-header__after-collapse__divider"></div>
        <div className="aso-header__after-collapse__filter-checkbox">
          <CheckboxInput label="Display only ASA keywords" onChange={handleFilterChanged('onlyASAKeywords')} />
          <CheckboxInput label="Display ASA columns" onChange={handleFilterChanged('displayASAColumn')} />
        </div>
      </div>
    </div>
  )
}

export default ASOFilters
